import { useContext, useState, useEffect, useMemo, useRef } from "react";
import { Container } from "../widgets";
import { EditorContext } from "../context/EditorContext";
import { getDropZone } from "../utils/dragAndDrop";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Clear';
import DeleteModal from '../../../common/DeleteModal';


/** @typedef {import("../widgets").BaseWidget} BaseWidget */

/**
 * Renders an overview tree/browser of widgets in the editor canvas. 
 * 
 * @param {object} props
 * @param {BaseWidget[]} props.widgets
 * @param {(widget: BaseWidget) => void} props.onClickWidget - Callback when a widget is clicked
 * @param {(widget: BaseWidget) => void} props.onDeleteWidget - Callback when a widget is deleted
 * @param {(widget: BaseWidget|null) => void} props.onMouseOver - Callback when a widget is hovered
 * @returns 
 */
export const TreeView = ({ widgets, onClickWidget, onDeleteWidget, onMouseOver }) => {

    const editorCtx = useContext(EditorContext);

    const storageKey = editorCtx?.reportId
        ? `tree-collapsed-${editorCtx.reportId}`
        : null;

    /** @type {[Set<string>, React.Dispatch<React.SetStateAction<Set<string>>>]} */
    const [collapsed, setCollapsed] = useState(() => {
        if (storageKey) {
            try {
                const stored = localStorage.getItem(storageKey);
                if (stored) return new Set(JSON.parse(stored));
            } catch {
                /* Fall through to default */
            }
        }
        /* Default: collapse all non-root containers */
        return new Set(
            widgets
                .filter(w => w instanceof Container && w.parent !== null)
                .map(w => w.id)
        );
    });

    /**
     * Toggles the collapsed state of a container node.
     * @param {string} id
     */
    const toggleCollapsed = (id) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            if (storageKey) {
                localStorage.setItem(storageKey, JSON.stringify([...next]));
            }
            return next;
        });
    };

    /**
     * Handles dropping a widget in the tree view. 
     * @param {React.DragEvent} e
     * @param {BaseWidget} targetWidget
     * @param {'before'|'after'|'inside'|null} dropEdge
     */
    const handleDrop = (e, targetWidget, dropEdge) => {
        if (!editorCtx || !dropEdge) {
            return;
        }

        const droppedId = e.dataTransfer.getData('id');
        const type = e.dataTransfer.getData('type');

        const droppedWidget = editorCtx.getWidgetById(droppedId);

        if (droppedWidget?.id === targetWidget.id) {
            return;
        }

        /** @type {string|null} */
        let parentId = targetWidget.parent;
        let targetIndex = 0;

        if (dropEdge === 'inside' && targetWidget instanceof Container) {
            parentId = targetWidget.id;
            targetIndex = widgets
                .filter(w => w.parent === targetWidget.id)
                .filter(w => w.deleted !== true)
                .length;
        } else {
            const siblings = widgets
                .filter(w => w.parent === targetWidget.parent)
                .filter(w => w.deleted !== true)
                .sort((a, b) => a.order - b.order);

            const indexOfTarget = siblings.findIndex(w => w.id === targetWidget.id);

            if (indexOfTarget < 0) {
                return;
            }

            targetIndex = dropEdge === 'after'
                ? indexOfTarget + 1
                : indexOfTarget;
        }

        editorCtx.handleWidgetDrop({
            droppedId,
            type,
            parentId,
            targetIndex,
        });
    };

    /**
     * @param {string|null} parentId 
     * @param {number} level 
     * @returns {JSX.Element[]}
     */
    const renderTree = (parentId, level = 0) => {
        return widgets
            .filter(w => w.parent === parentId)
            .filter(w => w.deleted !== true)
            .sort((a, b) => a.order - b.order)
            .map(w => (
                <div key={w.id} 
                    className="report-builder-tree-container"
                    data-id={w.id}
                    >
                    <TreeNode 
                        widget={w} 
                        widgets={widgets}
                        level={level}
                        isActive={editorCtx?.activeWidgetId === w.id}
                        isCollapsed={collapsed.has(w.id)}
                        onDropWidget={(e, dropEdge) => {
                            handleDrop(e, w, dropEdge);
                        }}
                        onToggleCollapse={toggleCollapsed}
                        onClick={() => onClickWidget(w)}
                        onDelete={() => onDeleteWidget(w)}
                        onMouseOver={() => onMouseOver(w)}
                        onMouseOut={() => onMouseOver(null)}
                    />
                    {!collapsed.has(w.id) && renderTree(w.id, level + 1)}
                </div>
            ));
    };

    return <div className="report-builder-tree-view">
        {renderTree(null)}
    </div>;
};

/**
 * @param {object} props
 * @param {BaseWidget} props.widget
 * @param {BaseWidget[]} props.widgets
 * @param {number} props.level
 * @param {boolean} props.isActive
 * @param {boolean} props.isCollapsed
 * @param {(e: React.DragEvent, dropEdge: 'before'|'after'|'inside'|null) => void} props.onDropWidget
 * @param {(id: string) => void} props.onToggleCollapse
 * @param {() => void} props.onClick
 * @param {(widget: BaseWidget) => void} props.onDelete
 * @param {() => void} props.onMouseOver
 * @param {() => void} props.onMouseOut
 * @returns 
 */
export const TreeNode = ({
    widget,
    widgets,
    level,
    isActive,
    isCollapsed,
    onDropWidget,
    onToggleCollapse,
    onClick,
    onDelete,
    onMouseOver,
    onMouseOut,
}) => {

    const [dropEdge, setDropEdge] = useState(
        /** @type {'before'|'after'|'inside'|null} */ (null)
    );
    // Bevestigen via de DeleteModal van de app, niet met window.confirm
    // (opgemerkt 2026-10-05).
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    return (
        <>
        <div onMouseOver={onMouseOver}
            onMouseLeave={onMouseOut}
            draggable={true}
            data-id={widget.id}
            data-drop-edge={dropEdge ?? undefined}
            className={`report-builder-tree-node`}
            style={{ marginLeft: level * 16 }}
            onDragStart={(e) => {
                e.stopPropagation();
                e.dataTransfer.setData('id', widget.id);
                e.dataTransfer.setData('type', widget.constructor.name);
            }}
            onDragEnd={() => {
                setDropEdge(null);
            }}
            onDragOver={e => {
                e.stopPropagation();
                e.preventDefault();

                if (widget.parent === null) {
                    setDropEdge('inside');
                    return;
                }

                const rect = e.currentTarget.getBoundingClientRect();
                const zone = getDropZone({
                    pointer: e.clientY,
                    rect,
                    axis: 'y',
                    minThreshold: 8,
                });

                if (zone === 'inside' && widget instanceof Container) {
                    setDropEdge('inside');
                } else {
                    setDropEdge(zone === 'before' ? 'before' : 'after');
                }
            }}
            onDragLeave={e => {
                e.stopPropagation();
                setDropEdge(null);
            }}
            onDrop={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDropWidget(e, dropEdge);
                setDropEdge(null);
            }}
            >
            {widget instanceof Container && widgets.some(w => w.parent === widget.id && w.deleted !== true) && (
                <button
                    className="tree-node-collapse-btn"
                    onClick={e => {
                        e.stopPropagation();
                        onToggleCollapse(widget.id);
                    }}
                    aria-label={isCollapsed ? 'Uitklappen' : 'Inklappen'}
                >
                    {isCollapsed
                        ? <ChevronRightIcon />
                        : <ExpandMoreIcon />}
                </button>
            )}
            {widget.parent === null 
                ? <span className="root-indicator">Document</span>
                : <>{widget.getIcon()} {widget.getName()}</>
            }
            <button
                className="tree-node-btn"
                onClick={e => {
                    e.stopPropagation();
                    onClick();
                }}
                aria-label="Bewerken"
            >
                <EditIcon />
            </button>
            <button 
                className="tree-node-btn"
                onClick={e => {
                    e.stopPropagation();
                    setDeleteModalOpen(true);
                }}
                aria-label="Verwijderen"
            >
                <DeleteIcon />
            </button>
        </div>
        {/* Naast de rij en niet erin: klikken in de modal horen niet bij de
            sleep- en klikhandlers van de rij. */}
        {deleteModalOpen && (
            <DeleteModal
                deleteModal={deleteModalOpen}
                setDeleteModal={setDeleteModalOpen}
                deleteItem={() => {
                    setDeleteModalOpen(false);
                    onDelete(widget);
                }}
                deleteName={`de widget '${widget.getName()}'`}
                /* Not 'kan niet ongedaan': 'Ongedaan maken' restores it while
                   the editor is open (testronde 2026-10-15, B7). Leaving the
                   editor clears that history, and the next open removes it
                   for good (cleanupDeletedWidgets). */
                undoNote="Je kunt dit terugdraaien met 'Ongedaan maken' bovenaan het paneel, zolang je de bouwer open hebt."
            />
        )}
        </>
    );
}
